/**
 * Today.tsx — Role-aware dispatcher screen (FR-004).
 *
 * Reads useEffectiveRole() and useFeatureFlag('operator_command_center_v1').
 * Dispatches to one of four surfaces:
 *   1. role === null            -> <FullScreenSpinner/>
 *   2. flag === false           -> <WorkspaceList/>
 *   3. flag + Admin/Operator    -> <CommandCenter/>
 *   4. flag + other roles       -> <MyWorkLane/> (inside SubscriptionScope)
 *
 * Zero backend reads of its own. Single DEV-guarded debug log per render.
 */
import '../../styles/screens/today.css';
import React from "react";
import { useEffectiveRole } from "../../api/hooks/useRBAC";
import { useFeatureFlag } from "../../api/hooks/useFeatureFlag";
import FullScreenSpinner from "../../components/primitives/FullScreenSpinner";
import WorkspaceList from "../../screens/workspaces/WorkspaceList";
import SubscriptionScope from "../../components/layout/SubscriptionScope";
import { useEffectiveUser } from "../../api/hooks/useEffectiveUser";
import CommandCenter from "./CommandCenter";
import MyWorkLane from "./MyWorkLane";
import { useT } from "../../i18n/useT";

// Internal role identifiers compared against useEffectiveRole() — NOT display
// strings, so they must not be translated (and t() is unavailable at module scope).
const COMMAND_CENTER_ROLES = new Set(["Admin", "Operator"]);

export default function Today(): React.ReactElement {
  const t = useT();

  const role = useEffectiveRole();
  const user = useEffectiveUser();
  const flag = useFeatureFlag("operator_command_center_v1");

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug("[today.dispatcher.resolved] role=%s flag=%s", role, flag);
  }

  if (role === null) {
    return <FullScreenSpinner />;
  }

  if (!flag) {
    return <WorkspaceList />;
  }

  if (COMMAND_CENTER_ROLES.has(role)) {
    return <CommandCenter />;
  }

  return user ? (
    <SubscriptionScope key={user._id} active>
      <MyWorkLane />
    </SubscriptionScope>
  ) : null;
}
